import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  Typography,
  TextField,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Add, DirectionsBus, Edit, Create, PersonRemove, Person } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { sessionAPI, studentAPI, transportationAPI } from '../../services/api';
import AppHeader from '../AppHeader';

const STATUS_COLOR = {
  DRAFT: 'default',
  SENT: 'info',
  SIGNED: 'primary',
  COMPLETE: 'success',
};

const TransportationList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentSession, setCurrentSession] = useState(null);
  const [students, setStudents] = useState([]);
  const [trfs, setTrfs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [removeConfirm, setRemoveConfirm] = useState(null);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        let session = null;
        try {
          const res = await sessionAPI.getCurrentSession();
          session = res.data;
        } catch (_) {
          const sessRes = await sessionAPI.getSessions();
          const list = sessRes.data.results || sessRes.data || [];
          if (list.length > 0) {
            await sessionAPI.setActiveSession(list[0].id);
            const curRes = await sessionAPI.getCurrentSession();
            session = curRes.data;
          }
        }
        if (cancelled || !session?.id) {
          setLoading(false);
          return;
        }
        setCurrentSession(session);
        const [studentsRes, trfRes] = await Promise.all([
          studentAPI.getStudents(session.id),
          transportationAPI.list({ session: session.id }),
        ]);
        if (cancelled) return;
        const stuList = studentsRes.data.results || studentsRes.data || [];
        const stuArr = Array.isArray(stuList) ? stuList : [];
        const studentsUnique = Array.from(
          new Map(stuArr.map((s) => [s.id, s])).values()
        );
        setStudents(studentsUnique);

        const trfList = trfRes.data.results || trfRes.data || [];
        const trfArr = Array.isArray(trfList) ? trfList : [];
        const trfsUnique = Array.from(
          new Map(trfArr.map((tr) => [tr.student, tr])).values()
        );
        setTrfs(trfsUnique);
      } catch (e) {
        console.error('Transportation list load error:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const refreshStudents = useCallback(async () => {
    if (!currentSession?.id) return;
    try {
      const studentsRes = await studentAPI.getStudents(currentSession.id);
      const stuList = studentsRes.data.results || studentsRes.data || [];
      const stuArr = Array.isArray(stuList) ? stuList : [];
      setStudents(Array.from(new Map(stuArr.map((s) => [s.id, s])).values()));
    } catch (e) {
      console.error('Refresh students error:', e);
    }
  }, [currentSession?.id]);

  const handleRemoveFromSession = async () => {
    if (!removeConfirm) return;
    setRemoving(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      await studentAPI.updateStudent(removeConfirm.id, { discharge_date: today, is_active: false });
      setRemoveConfirm(null);
      await refreshStudents();
    } catch (e) {
      console.error('Remove from session error:', e);
    } finally {
      setRemoving(false);
    }
  };

  const trfByStudentId = useMemo(() => {
    const map = {};
    trfs.forEach((tr) => {
      const sid = tr.student != null ? tr.student : tr.student_id;
      if (sid != null && map[sid] == null) map[sid] = tr;
    });
    return map;
  }, [trfs]);

  const rows = useMemo(() => {
    const seen = new Set();
    return students
      .filter((s) => {
        if (seen.has(s.id)) return false;
        seen.add(s.id);
        return true;
      })
      .map((s) => ({
        student: s,
        trf: trfByStudentId[s.id],
        status: trfByStudentId[s.id]?.status || null,
      }));
  }, [students, trfByStudentId]);

  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const lower = search.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (r.student.last_name || '').toLowerCase().includes(lower) ||
        (r.student.first_name || '').toLowerCase().includes(lower)
    );
  }, [rows, search]);

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Box>
      <AppHeader backTo={null} />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DirectionsBus /> Transportation Request Forms
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search by name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 200 }}
            />
            <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/transportation/new')}>
              New TRF
            </Button>
          </Box>
        </Box>
        {currentSession && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Session: {currentSession.display_name || currentSession.name}
          </Typography>
        )}
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Student Name</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>Mother Cell</TableCell>
                <TableCell>Father Cell</TableCell>
                <TableCell>Parent Email</TableCell>
                <TableCell>TRF Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map((row) => (
                <TableRow key={row.student.id} hover>
                  <TableCell
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/transportation/${row.student.id}`)}
                  >
                    {row.student.last_name}, {row.student.first_name}
                  </TableCell>
                  <TableCell>{row.student.class_num || '—'}</TableCell>
                  <TableCell>{row.student.mother_cell ?? row.student.mother_phone ?? '—'}</TableCell>
                  <TableCell>{row.student.father_cell ?? row.student.father_phone ?? '—'}</TableCell>
                  <TableCell>{row.student.parent_email ?? row.student.email ?? '—'}</TableCell>
                  <TableCell>
                    {row.status ? (
                      <Chip label={row.status} color={STATUS_COLOR[row.status] || 'default'} size="small" />
                    ) : (
                      <Typography variant="body2" color="text.secondary">No TRF</Typography>
                    )}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        startIcon={<Person />}
                        variant="outlined"
                        onClick={() => navigate(`/students/${row.student.id}`)}
                      >
                        View student
                      </Button>
                      {row.trf ? (
                        <Button
                          size="small"
                          startIcon={<Edit />}
                          variant="outlined"
                          onClick={() => navigate(`/transportation/${row.student.id}`)}
                        >
                          View TRF
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          startIcon={<Create />}
                          variant="contained"
                          onClick={() => navigate(`/transportation/${row.student.id}`)}
                        >
                          Create TRF
                        </Button>
                      )}
                      {(user?.role === 'superadmin' || user?.role === 'admin') && (
                        <Button
                          size="small"
                          startIcon={<PersonRemove />}
                          variant="outlined"
                          color="error"
                          onClick={() => setRemoveConfirm(row.student)}
                        >
                          Remove from Session
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {filteredRows.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            {students.length === 0 ? 'No students in this session.' : 'No students match your search.'}
          </Typography>
        )}

        <Dialog open={!!removeConfirm} onClose={() => !removing && setRemoveConfirm(null)}>
          <DialogTitle>Remove from session</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Remove {removeConfirm ? `${removeConfirm.first_name} ${removeConfirm.last_name}` : ''} from {currentSession?.display_name || currentSession?.name}? This will set their discharge date and mark them inactive. You can re-add them later if needed.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRemoveConfirm(null)} disabled={removing}>Cancel</Button>
            <Button color="error" variant="contained" onClick={handleRemoveFromSession} disabled={removing}>
              {removing ? 'Removing...' : 'Remove'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default TransportationList;
